import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { AlertFloating } from '@/components/application/alerts/alerts';
import { Avatar } from '@/components/base/avatar/avatar';
import { Badge } from '@/components/base/badges/badges';
import { Dropdown } from '@/components/base/dropdown/dropdown';
import { Input } from '@/components/base/input/input';
import { ConfirmationModal } from '@/components/base/modals/confirmation-modal';
import { notify } from '@/components/base/notifications/notifications';
import { queryKeys } from '@/hooks/api/query-keys';
import { formatDate } from '@/utils/date';

import type {
  OrganizationMemberRow,
  OrganizationRole,
} from '../-utils/organization-settings-server';

import { organizationMembersQueryOptions } from '../-utils/organization-settings-query';
import {
  changeOrganizationMemberRole,
  removeOrganizationMember,
  transferOrganizationOwnership,
} from '../-utils/organization-settings-server';
import { SettingsSection } from '../../settings/-components/settings-section';
import { SETTINGS_HELP } from '../../settings/-utils/help-articles';

export const Route = createFileRoute(
  '/researchers/_authenticated/organization-settings/organization/members',
)({
  component: OrganizationMembersPage,
  head: () => ({ meta: [{ title: t`Manage users` }] }),
});

function roleLabel(role: OrganizationRole): string {
  switch (role) {
    case 'owner':
      return t`Owner`;
    case 'admin':
      return t`Admin`;
    case 'member':
      return t`Member`;
  }
}

const fullName = (m: { firstName: string; lastName: string }) =>
  `${m.firstName} ${m.lastName}`.trim();

type PendingAction =
  | { kind: 'role'; member: OrganizationMemberRow; role: 'admin' | 'member' }
  | { kind: 'transfer'; member: OrganizationMemberRow }
  | { kind: 'remove'; member: OrganizationMemberRow };

function OrganizationMembersPage() {
  const { activeDashboard } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const members = useQuery(
    organizationMembersQueryOptions(activeDashboard.organization ?? ''),
  );
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<PendingAction | null>(null);

  const act = useMutation({
    mutationFn: async (action: PendingAction) => {
      const userId = action.member.id;
      const result =
        action.kind === 'role'
          ? await changeOrganizationMemberRole({
              data: { userId, role: action.role },
            })
          : action.kind === 'transfer'
            ? await transferOrganizationOwnership({ data: { userId } })
            : await removeOrganizationMember({ data: { userId } });
      if (!result.success) {
        throw new Error(result.message ?? t`Please try again.`);
      }
      return action;
    },
    onSuccess: async (action) => {
      setPending(null);
      const name = fullName(action.member);
      notify({
        title:
          action.kind === 'role'
            ? t`${name} is now ${roleLabel(action.role).toLowerCase()}`
            : action.kind === 'transfer'
              ? t`${name} is now the organization owner`
              : t`${name} was removed from the organization`,
        color: 'success',
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.workspaceSettings.all(),
      });
    },
    onError: (error) =>
      notify({
        title: t`Couldn't update the organization`,
        description: error.message,
        color: 'error',
      }),
  });

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (members.data?.members ?? []).filter(
      (m) =>
        !needle ||
        fullName(m).toLowerCase().includes(needle) ||
        m.email.toLowerCase().includes(needle),
    );
  }, [members.data, search]);

  return (
    <SettingsSection
      title={t`Manage users`}
      description={t`Everyone in your organization and their role.`}
      helpUrl={SETTINGS_HELP.teams}
    >
      {members.isError ? (
        <AlertFloating
          color="error"
          title={t`We couldn't load the organization's users`}
          description={t`Refresh the page to try again.`}
          confirmLabel=""
        />
      ) : members.isPending ? (
        <div className="h-48 animate-pulse rounded-lg bg-secondary" />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Input
              aria-label={t`Search users`}
              placeholder={t`Search by name or email`}
              value={search}
              onChange={setSearch}
              size="xs"
              className="max-w-xs"
            />
            <p className="text-sm text-tertiary">
              {members.data.members.length === 1
                ? t`1 user`
                : t`${members.data.members.length} users`}
            </p>
          </div>
          <ul className="divide-y divide-secondary">
            {visible.map((member) => {
              const isSelf = member.id === members.data.currentUserId;
              const isOwner = member.role === 'owner';
              const canChangeRole =
                members.data.can.changeRole && !isOwner && !isSelf;
              const canTransfer =
                members.data.can.transferOwnership && !isOwner;
              const canRemove = members.data.can.remove && !isOwner && !isSelf;
              return (
                <li key={member.id} className="flex items-center gap-3 py-3">
                  <Avatar
                    size="md"
                    src={member.picture}
                    alt={fullName(member)}
                  />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-primary">
                        {fullName(member)}
                        {isSelf && (
                          <span className="font-normal text-tertiary">
                            {' '}
                            <Trans>(you)</Trans>
                          </span>
                        )}
                      </p>
                      <Badge
                        type="pill-color"
                        color={isOwner ? 'brand' : 'gray'}
                        size="sm"
                      >
                        {roleLabel(member.role)}
                      </Badge>
                    </div>
                    <p className="truncate text-sm text-tertiary">
                      {member.email}
                    </p>
                  </div>
                  {member.lastLoginAt && (
                    <p className="hidden text-sm text-tertiary md:block">
                      <Trans>Last login {formatDate(member.lastLoginAt)}</Trans>
                    </p>
                  )}
                  <div className="w-8 shrink-0">
                    {(canChangeRole || canTransfer || canRemove) && (
                      <Dropdown.Root>
                        <Dropdown.DotsButton
                          aria-label={t`Actions for ${fullName(member)}`}
                        />
                        <Dropdown.Popover>
                          <Dropdown.Menu
                            onAction={(key) => {
                              if (key === 'make-admin') {
                                setPending({
                                  kind: 'role',
                                  member,
                                  role: 'admin',
                                });
                              } else if (key === 'make-member') {
                                setPending({
                                  kind: 'role',
                                  member,
                                  role: 'member',
                                });
                              } else if (key === 'transfer') {
                                setPending({ kind: 'transfer', member });
                              } else if (key === 'remove') {
                                setPending({ kind: 'remove', member });
                              }
                            }}
                          >
                            {canChangeRole && member.role === 'member' && (
                              <Dropdown.Item
                                id="make-admin"
                                label={t`Make admin`}
                              />
                            )}
                            {canChangeRole && member.role === 'admin' && (
                              <Dropdown.Item
                                id="make-member"
                                label={t`Make member`}
                              />
                            )}
                            {canTransfer && (
                              <Dropdown.Item
                                id="transfer"
                                label={t`Transfer ownership`}
                              />
                            )}
                            {canRemove && (
                              <Dropdown.Item
                                id="remove"
                                label={t`Remove from organization`}
                              />
                            )}
                          </Dropdown.Menu>
                        </Dropdown.Popover>
                      </Dropdown.Root>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <ConfirmationModal
        isOpen={pending !== null}
        onOpenChange={(open) => !open && setPending(null)}
        title={
          pending
            ? pending.kind === 'role'
              ? t`Make ${fullName(pending.member)} ${roleLabel(pending.role).toLowerCase()}?`
              : pending.kind === 'transfer'
                ? t`Transfer ownership to ${fullName(pending.member)}?`
                : t`Remove ${fullName(pending.member)} from the organization?`
            : ''
        }
        description={
          pending?.kind === 'transfer'
            ? t`They become the organization owner, and you become an admin.`
            : pending?.kind === 'remove'
              ? t`They're also removed from every team in the organization.`
              : pending?.kind === 'role' && pending.role === 'admin'
                ? t`Admins can manage the organization's users, teams and settings.`
                : undefined
        }
        confirmLabel={
          pending?.kind === 'transfer'
            ? t`Transfer ownership`
            : pending?.kind === 'remove'
              ? t`Remove`
              : t`Change role`
        }
        isDestructive={pending?.kind === 'remove'}
        isLoading={act.isPending}
        onConfirm={() => pending && act.mutate(pending)}
        onCancel={() => setPending(null)}
      />
    </SettingsSection>
  );
}
